import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import './ThreeAnim.css';

import * as THREE from "three";

import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass";
import {Vector2} from "three";
import {EffectComposer} from "three/examples/jsm/postprocessing/EffectComposer";
import {RenderPass} from "three/examples/jsm/postprocessing/RenderPass";

import MapScene from './assets/gltf/map_scene_punta_garrobo.glb';
import opacityMap from './assets/gltf/map_graphic_punta_garrobo_opacity.jpg';

const checkRefs = (refs) => {
    return new Promise((resolve) => {
        const interval = setInterval(() => {
            const allRefsReady = refs.every(ref => ref.current);
            if (allRefsReady) {
                clearInterval(interval);
                resolve();
            }
        }, 100); // Check every 100ms
    });
};

const ThreeAnim = forwardRef((props, ref) => {

    const filterIsDirtyRef = useRef(true);
    //const [frameArr, setFrameArr] = useState([]);
    const threeOverlayElemDivRef = useRef(null);
    const frameElemDivRef = useRef(null);
    const frameElemImgRef = useRef(null);

    const localFilterOverlaySceneRef = useRef(null);

    const [applySceneFilter, setApplySceneFilter] = useState(null);
    const [overlayItemsReady, setOverlayItemsReady] = useState(false);

    const [loadComplete, setLoadComplete] = useState(false);

    const {onItemSelected, projectData, sceneFilter, onTooltipActivated} = props;

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        onItemSelected: onItemSelected,
        callFilterOverlayScene: (args) => {
            if (localFilterOverlaySceneRef.current) {
                localFilterOverlaySceneRef.current(args);
            }
        },
        offsetZoom: (v) => {
            OffsetZoom(v);
        },
        loadComplete: loadComplete
    }));

    const offsetZoomRef = useRef(null);

    function OffsetZoom(v) {
        if(offsetZoomRef.current !== null){
            offsetZoomRef.current(v);
        }
    }


    useEffect(() => {
        //console.log("PROJECT DATA CHANGED");
        //console.log('applySceneFilter: ', applySceneFilter);
        //console.log('projectData: ', projectData);
        //console.log('sceneFilter: ', sceneFilter);
        if (overlayItemsReady === true && applySceneFilter !== null && applySceneFilter !== undefined && projectData !== null && sceneFilter !== null) {
            applySceneFilter(projectData, sceneFilter);
            //console.log('RAN SCENE FILTER UPDATE ON THREEANIM.JS');
        }


    }, [projectData, sceneFilter, applySceneFilter, overlayItemsReady]);


    useEffect(() => {
        const refs = [threeOverlayElemDivRef, frameElemDivRef, frameElemImgRef]

        const checkAllRefs = async () => {
            console.log('Checking refs are ready');
            await checkRefs(refs);
            console.log('All refs are ready');
            const threeOverlayElemDiv = threeOverlayElemDivRef.current;
            const frameElemDiv = frameElemDivRef.current;
            const frameElemImg = frameElemImgRef.current;

            //Mouse trackers
            let mouseIsDown = false;
            let mouseDownTime = Date.now();

            function OnPointerDownInFrames(e) {
                let clientX = e.clientX ?? e.touches?.[0]?.clientX;
                let clientY = e.clientY ?? e.touches?.[0]?.clientY;

                let overlayViewportRect = threeOverlayElemDiv.getBoundingClientRect();
                overlayViewportX = ((clientX - overlayViewportRect.left) / overlayViewportRect.width) * 2 - 1;
                overlayViewportY = -((clientY - overlayViewportRect.top) / overlayViewportRect.height) * 2 + 1;

                mouseIsDown = true;
                mouseDownTime = Date.now();
            }

            function OnPointerMoveInFrames(e) {
                let clientX = e.clientX ?? e.touches?.[0]?.clientX;
                let clientY = e.clientY ?? e.touches?.[0]?.clientY;

                let overlayViewportRect = threeOverlayElemDiv.getBoundingClientRect();
                overlayViewportX = ((clientX - overlayViewportRect.left) / overlayViewportRect.width) * 2 - 1;
                overlayViewportY = -((clientY - overlayViewportRect.top) / overlayViewportRect.height) * 2 + 1;
            }

            function OnPointerUpInFrames(e) {
                mouseIsDown = false;
                let clientX = e.clientX ?? e.touches?.[0]?.clientX;
                let clientY = e.clientY ?? e.touches?.[0]?.clientY;

                let overlayViewportRect = threeOverlayElemDiv.getBoundingClientRect();
                overlayViewportX = ((clientX - overlayViewportRect.left) / overlayViewportRect.width) * 2 - 1;
                overlayViewportY = -((clientY - overlayViewportRect.top) / overlayViewportRect.height) * 2 + 1;

                if(Date.now() - mouseDownTime < 300) {
                    onItemSelected(overlaySelectedItem);
                }
            }

            // Attach event listeners to support both mouse and touch
            //document.addEventListener("pointerdown", OnPointerDownInFramesDocument);
            //document.addEventListener("pointermove", OnPointerMoveInFramesDocument);
            //document.addEventListener("pointerup", OnPointerUpInFramesDocument);

// Function to get the world-space position of a specific frustum corner
            function getWorldPosition(ndcX, ndcY, camera) {
                const ndcPosition = new THREE.Vector3(ndcX, ndcY, 0.5); // Z = 0.5 for near plane
                ndcPosition.unproject(camera); // Convert from NDC to world space
                return ndcPosition;
            }

// Function to cast a ray from the camera to a specific frustum corner
            function castRayAlongFrustum(camera, targetPosition, mesh) {
                if (mesh) {
                    const raycaster = new THREE.Raycaster();

                    // Ray origin is the camera position
                    const origin = camera.position;

                    // Ray direction points to the target position (frustum corner)
                    const direction = targetPosition.clone().sub(origin).normalize();

                    raycaster.set(origin, direction);
                    return raycaster.intersectObject(mesh);
                }
            }

            // Define the downward direction for all rays
            const downwardDirection = new THREE.Vector3(0, -1, 0);

            // Create a small red box to move to intersection points
            function createRedBox(scene) {
                const boxGeometry = new THREE.BoxGeometry(1, 1, 1); // Small box
                const boxMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
                const boxMesh = new THREE.Mesh(boxGeometry, boxMaterial);
                scene.add(boxMesh);
                return boxMesh;
            }

            // Function to update red box position
            function updateBoxPosition(newPos, label, redBox) {
                redBox.position.copy(newPos);

            }

            //Pan
            // Function to get mouse position on a specific plane
            const groundPlane = new THREE.Plane(new THREE.Vector3(0,1,0), 0);
            const groundPlaneRaycaster = new THREE.Raycaster();
            let isPanning = false;
            let cameraPosPanStart = new THREE.Vector3();
            let clickPosPanStart = new THREE.Vector3();
            let clickPosPanCurrent = new THREE.Vector3();
            const cameraOffset = new THREE.Vector3();


            function getMousePositionOnGroundPlane(event) {
                const mouse = new THREE.Vector2();
                mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
                mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
                groundPlaneRaycaster.setFromCamera(mouse, camera);
                const intersection = new THREE.Vector3();
                groundPlaneRaycaster.ray.intersectPlane(groundPlane, intersection);

                return intersection;
            }

            const mousePosition = new THREE.Vector3(); // Object to store mouse coordinates
            const mousePositionPanStart = new THREE.Vector3(); // Object to store mouse coordinates

            const handlePanStart = (event) => {
                if (event.pointerType === "mouse" && event.button !== 0) return; // Ignore right-clicks

                isPanning = true;

                mousePositionPanStart.x = event.clientX ?? event.touches?.[0]?.clientX; // Horizontal position (pixels)
                mousePositionPanStart.z = event.clientY ?? event.touches?.[0]?.clientY; // Vertical position (pixels)
                mousePosition.x = event.clientX ?? event.touches?.[0]?.clientX; // Horizontal position (pixels)
                mousePosition.z = event.clientY ?? event.touches?.[0]?.clientY; // Vertical position (pixels)

                cameraPosPanStart.copy(camera.position);

                //clickPosPanStart = getMousePositionOnGroundPlane(event);
            }

            function clamp(value, min, max) {
                return Math.max(min, Math.min(max, value));
            }

            const handlePanMove = (event) => {
                mousePosition.x = event.clientX ?? event.touches?.[0]?.clientX; // Horizontal position (pixels)
                mousePosition.z = event.clientY ?? event.touches?.[0]?.clientY; // Vertical position (pixels)
            };

            const handlePanEnd = (event) => {
                if (event.pointerType === "mouse" && event.button !== 0) return; // Ignore right-click

                isPanning = false;

                //camera
                //cameraOffset.set(0,0,0);
            };
            //Zoom
            const zoomOutLimit = 1.4;
            const zoomInLimit = 3.4;
            const zoomSpeed = 0.001; // Adjust this value to control zoom speed
            function OnMouseWheelInFrames(e) {
                if (camera && mapPlaneBox) {
                    //const delta = Math.sign(e.deltaY); // -1 for zoom in, 1 for zoom out
                    //OffsetZoomUseEffect(delta);
                    let zoomDelta = -e.deltaY * zoomSpeed;
                    camera.zoom += zoomDelta;//clamp(newZoom, zoomOutLimit, zoomInLimit);
                    camera.zoom = clamp(camera.zoom, zoomOutLimit, zoomInLimit);
                    //console.log(camera.zoom);

                    // Calculate the 3D positions of the 2D screen corners in world space
                    const top = getWorldPosition(0, 1, camera);
                    const bottom = getWorldPosition(0, -1, camera);
                    const left = getWorldPosition(-1, 0, camera);
                    const right = getWorldPosition(1, 0, camera);

                    if (left.x < mapPlaneBox.min.x || right.x > mapPlaneBox.max.x) {
                        //console.log('HAS EXITED BORDER (X AXIS)');
                        camera.position.x = clamp(camera.position.x, mapPlaneBox.min.x * camera.zoom * zoomCompensate, mapPlaneBox.max.x * camera.zoom * zoomCompensate);
                        camera.updateProjectionMatrix();
                    }
                    if (bottom.z > mapPlaneBox.max.z || top.z < mapPlaneBox.min.z) {
                        //console.log('HAS EXITED BORDER (Z AXIS)');
                        camera.position.z = clamp(camera.position.z, mapPlaneBox.min.z * camera.zoom * zoomCompensate, mapPlaneBox.max.z * camera.zoom * zoomCompensate);
                        camera.updateProjectionMatrix();
                    }
                }
            }

            function OffsetZoomUseEffect(v) {
                if (camera && mapPlaneBox) {
                    camera.zoom += (-v * 0.2);
                    camera.zoom = clamp(camera.zoom, zoomOutLimit, zoomInLimit);


                    // Calculate the 3D positions of the 2D screen corners in world space
                    const top = getWorldPosition(0, 1, camera);
                    const bottom = getWorldPosition(0, -1, camera);
                    const left = getWorldPosition(-1, 0, camera);
                    const right = getWorldPosition(1, 0, camera);

                    if (left.x < mapPlaneBox.min.x || right.x > mapPlaneBox.max.x) {
                        //console.log('HAS EXITED BORDER (X AXIS)');
                        camera.position.x = clamp(camera.position.x, mapPlaneBox.min.x * camera.zoom * zoomCompensate, mapPlaneBox.max.x * camera.zoom * zoomCompensate);
                        camera.updateProjectionMatrix();
                    }
                    if (bottom.z > mapPlaneBox.max.z || top.z < mapPlaneBox.min.z) {
                        //console.log('HAS EXITED BORDER (Z AXIS)');
                        camera.position.z = clamp(camera.position.z, mapPlaneBox.min.z * camera.zoom * zoomCompensate, mapPlaneBox.max.z * camera.zoom * zoomCompensate);
                        camera.updateProjectionMatrix();
                    }

                }
            }

            offsetZoomRef.current = OffsetZoomUseEffect;

            //Stop playing frames
            function OnPointerLeaveInFrames(e) {
                isPanning = false;
            }

            frameElemDiv.addEventListener("pointerdown", OnPointerDownInFrames);
            frameElemDiv.addEventListener("pointermove", OnPointerMoveInFrames);
            frameElemDiv.addEventListener("pointerup", OnPointerUpInFrames);
            frameElemDiv.addEventListener("pointerleave", OnPointerLeaveInFrames);
            frameElemDiv.addEventListener("wheel", OnMouseWheelInFrames);
            //pan
            frameElemDiv.addEventListener('pointerdown', handlePanStart);
            frameElemDiv.addEventListener('pointermove', handlePanMove);
            frameElemDiv.addEventListener('pointerup', handlePanEnd);

            //THREE OVERLAY

            let fbxCameraUrl = "gltf/map_scene_punta_garrobo.glb";
            let fbxCameraName = "camera";
            let cameraMixer = null;
            let cameraClipAction = null;
            let farPlane = 100000;
            let mapPlane;
            let mapPlaneBox;
            let panCollider;
            //
            let overlayModelUrl = "/assets/gltf/map_scene_punta_garrobo.glb";
            let overlayItemsAll = [];
            let overlayItemsOn = [];
            //let overlayItemsReady = false;
            let overlayMaterials = {};
            let overlayHighlightMaterials = {};

            //Colors
            let defaultColor = 0xffa6a6;
            let defaultOpacity = 0.3;
            let highlightColor = 0xffa6a6;
            let highlightOpacity = 0.75;
            let baseVisibleOutlineColor = 0x000000;
            let baseHiddenOutlineColor = 0x000000;
            let highlightVisibleOutlineColor = 0xffbbbb;
            let highlightHiddenOutlineColor = 0xffbbbb;

            //Filter Function
            let scene = new THREE.Scene();

            //let x1 = createRedBox(scene);
            //x1.material = new THREE.MeshStandardMaterial();
            //let x2 = createRedBox(scene);
            //let x3 = createRedBox(scene);
            //let x4 = createRedBox(scene);

            //let camera = new THREE.OrthographicCamera(-1,1,1,-1,farPlane + 1);
            const aspect = window.innerWidth / window.innerHeight;
            const frustumSize = 100; // Controls the size of the orthographic view

            const camera = new THREE.OrthographicCamera(
                -frustumSize * aspect / 2, // left
                frustumSize * aspect / 2,  // right
                frustumSize / 2,           // top
                -frustumSize / 2,          // bottom
                0.1,                       // near
                1000                       // far
            );
            camera.position.set(0, 5, 0); // Position the camera
            camera.lookAt(0, 0, 0);       // Make the camera look at the scene center
            scene.add(camera);
            let ambientLight = new THREE.AmbientLight(0xFFFFFF, 1.5);
            scene.add(ambientLight);

            let gltfLoader = new GLTFLoader();
            const textureLoader = new THREE.TextureLoader();
            //Load camera animation, attach own camera as child
            /*fbxLoader.load(fbxCameraUrl, function (object) {
                cameraMixer = new THREE.AnimationMixer(object);
                cameraClipAction = cameraMixer.clipAction(object.animations[0]);
                scene.add(object);
                //object.scale.set(1, 1, 1);
                let fbxCamera = object.getObjectByName(fbxCameraName);
                camera.position.x = fbxCamera.position.x;
                camera.position.y = fbxCamera.position.y;
                camera.position.z = fbxCamera.position.z;
                camera.rotation.x = fbxCamera.rotation.x;
                camera.rotation.y = fbxCamera.rotation.y;
                camera.rotation.z = fbxCamera.rotation.z;
                //fbxCamera.attach(camera);
                cameraClipAction.play();
            });*/

            //Load building overlay model
            gltfLoader.load(MapScene, function (gltf) {

                gltf.scene.traverse(function (child) {
                    if (child instanceof THREE.Mesh && child.name !== 'map' && child.name !== 'collider') {
                        //console.log(child.material);
                        let childMaterial = new THREE.MeshToonMaterial(
                            {color: child.material.color, transparent: true, opacity: child.material.opacity});
                        let childHighlightMaterial = new THREE.MeshToonMaterial(
                            {color: child.material.color, transparent: true, opacity: highlightOpacity});
                        overlayItemsAll.push(child);
                        overlayItemsOn.push(child);
                        overlayMaterials[child.id] = childMaterial;
                        overlayHighlightMaterials[child.id] = childHighlightMaterial;

                        child.material = childMaterial;
                    } else if (child.name === 'map') {
                        const originalTexture = child.material.map;
                        // Ensure texture encoding matches renderer encoding
                        originalTexture.colorSpace = THREE.LinearSRGBColorSpace;
                        originalTexture.minFilter = THREE.LinearMipMapLinearFilter;  // Smooth for scaling down (minification)
                        originalTexture.magFilter = THREE.LinearFilter;  // Smooth for scaling up (magnification)

                        //originalTexture.generateMipmaps = false;
                        let childMaterial = new THREE.MeshBasicMaterial({map: originalTexture});
                        child.material = childMaterial;
                        mapPlane = child;
                        //mapPlane.visible = false;
                        //console.log('WILL RUN TEX LOAD');
                        textureLoader.load(opacityMap, (tex) => {
                            mapPlane.position.y = -0.1;
                            mapPlane.material.alphaMap = tex;
                            mapPlane.material.transparent = true; // Ensure transparency is enabled
                            mapPlane.material.needsUpdate = true; // Force material update
                        }, ()=>{}, (error) => {
                            // Log more detailed error information
                            console.error('Error loading texture:', error);

                            if (error && error.target && error.target.status) {
                                console.error(`HTTP Status Code: ${error.target.status}`);
                                console.error(`Response URL: ${error.target.responseURL}`);
                            }
                        });
                    } else if (child.name === 'collider') {
                        panCollider = child;
                        panCollider.visible = false;

                    }
                    setOverlayItemsReady(true);
                });
                scene.add(gltf.scene);
                if (gltf.cameras && gltf.cameras.length > 0) {
                    //camera.position.copy(gltf.cameras[0].position);
                    //camera.near = 1;
                    //camera.far = farPlane;
                    //start zoom val
                    camera.position.x = 0;
                    camera.position.z = 20;
                    camera.zoom = 1.6;
                }
                //panCollider.position.y = 10;
                //panCollider.material = new THREE.MeshBasicMaterial();
                mapPlaneBox = new THREE.Box3().setFromObject(panCollider);
            });



            //Raycaster params
            let overlayRaycaster = new THREE.Raycaster();
            let overlayViewportX = 0;
            let overlayViewportY = 0;
            let overlaySelectedItem = null;

            //Renderer setup
            let renderer = new THREE.WebGLRenderer({alpha: true, outputEncoding: THREE.SRGBColorSpace});
            threeOverlayElemDiv.appendChild(renderer.domElement);
            //Render Passes
            let effectComposer = new EffectComposer(renderer);
            const renderPass = new RenderPass(scene, camera);

            let outlinePassBase = new OutlinePass(
                new Vector2(threeOverlayElemDiv.offsetWidth, threeOverlayElemDiv.offsetHeight), scene, camera, overlayItemsOn);
            renderer.setSize(threeOverlayElemDiv.offsetWidth, threeOverlayElemDiv.offsetHeight);

            let outlinePassHighlight = new OutlinePass(
                new Vector2(threeOverlayElemDiv.offsetWidth, threeOverlayElemDiv.offsetHeight), scene, camera);
            renderer.setSize(threeOverlayElemDiv.offsetWidth, threeOverlayElemDiv.offsetHeight);

            outlinePassBase.edgeStrength = 3.0;
            outlinePassBase.edgeGlow = 0.0;
            outlinePassBase.edgeThickness = 0.3;
            outlinePassBase.pulsePeriod = 0;
            outlinePassBase.rotate = false;
            outlinePassBase.usePatternTexture = false;
            outlinePassBase.visibleEdgeColor.set(baseVisibleOutlineColor);
            outlinePassBase.hiddenEdgeColor.set(baseHiddenOutlineColor);


            outlinePassHighlight.edgeStrength = 3.0;
            outlinePassHighlight.edgeGlow = 0.0;
            outlinePassHighlight.edgeThickness = 1.0;
            outlinePassHighlight.pulsePeriod = 0;
            outlinePassHighlight.rotate = false;
            outlinePassHighlight.usePatternTexture = false;
            outlinePassHighlight.visibleEdgeColor.set(highlightVisibleOutlineColor);
            outlinePassHighlight.hiddenEdgeColor.set(highlightHiddenOutlineColor);

            effectComposer.addPass(renderPass);
            effectComposer.addPass(outlinePassBase);
            effectComposer.addPass(outlinePassHighlight);

            //RE-PROJECTION effects style decisions in html! Overlay style must work together with projection.
            function RefreshCameraProjectionPerspective() {
                if (camera != null) {
                    //use frame / background image as ref on how to re-project
                    let frameElemImgRect = frameElemImg.getBoundingClientRect();
                    camera.aspect = frameElemImgRect.width / frameElemImgRect.height;
                    camera.updateProjectionMatrix();
                    renderer.setSize(frameElemImgRect.width, frameElemImgRect.height);
                    outlinePassBase.setSize(frameElemImgRect.width, frameElemImgRect.height);
                    outlinePassHighlight.setSize(frameElemImgRect.width, frameElemImgRect.height);
                    effectComposer.setSize(frameElemImgRect.width, frameElemImgRect.height);
                }
            }

            function RefreshCameraProjection() {
                if (camera != null) {
                    // Use frame / background image as reference for resizing
                    let frameElemImgRect = frameElemImg.getBoundingClientRect();
                    const aspect = frameElemImgRect.width / frameElemImgRect.height;

                    if (camera.isPerspectiveCamera) {
                        // Update perspective camera
                        camera.aspect = aspect;
                        camera.updateProjectionMatrix();
                    } else if (camera.isOrthographicCamera) {
                        // Update orthographic camera
                        const frustumHeight = camera.top - camera.bottom; // Original height of the frustum
                        const frustumWidth = frustumHeight * aspect;      // Adjust width based on new aspect
                        const centerX = (camera.left + camera.right) / 2; // Center of the view
                        const centerY = (camera.top + camera.bottom) / 2;

                        // Recalculate boundaries
                        camera.left = centerX - frustumWidth / 2;
                        camera.right = centerX + frustumWidth / 2;
                        camera.top = centerY + frustumHeight / 2;
                        camera.bottom = centerY - frustumHeight / 2;
                        camera.updateProjectionMatrix();
                    }

                    // Update renderer and post-processing effects
                    renderer.setSize(frameElemImgRect.width, frameElemImgRect.height);
                    outlinePassBase.setSize(frameElemImgRect.width, frameElemImgRect.height);
                    outlinePassHighlight.setSize(frameElemImgRect.width, frameElemImgRect.height);
                    effectComposer.setSize(frameElemImgRect.width, frameElemImgRect.height);
                }
            }


            function worldToScreen(position, camera, renderer) {
                const vector = position.clone(); // Clone to avoid modifying the original vector
                vector.project(camera); // Convert world to NDC (-1 to +1)

                const halfWidth = renderer.domElement.clientWidth / 2;
                const halfHeight = renderer.domElement.clientHeight / 2;

                return {
                    x: (vector.x * halfWidth) + halfWidth,
                    y: -(vector.y * halfHeight) + halfHeight // Flip Y since screen coordinates are inverted
                };
            }



            const tooltipMapping = {
                tooltip_beach_club: 'BEACH CLUB',
                tooltip_las_palmitas: 'LAS PALMITAS',
                tooltip_lighthouse: 'LIGHTHOUSE',
                tooltip_lookout_point_a: 'LOOKOUT POINT',
                tooltip_lookout_point_b: 'LOOKOUT POINT',
                tooltip_main_entrance: 'MAIN ENTRANCE',
                tooltip_playa_las_gatas: 'BEACH ACCESS / PLAYA LAS GATAS',
                tooltip_restaurant_sales_center: 'RESTAURANT / SALES CENTER',
                tooltip_secondary_entrance: 'SECONDARY ENTRANCE',
                tooltip_tennis_padel: 'TENNIS / PADLE'
            };



            function RaycastCheck() {
                //Update mouse position based raycasting parameters
                overlayRaycaster.setFromCamera(new THREE.Vector2(overlayViewportX, overlayViewportY), camera);
                let intersectedItems = overlayRaycaster.intersectObjects(overlayItemsOn);
                //console.log(overlayViewportX, overlayViewportY);
                //check if there are no intersects, if here are not, return current hovered item to original state and release it
                if (intersectedItems.length > 0) {
                    //only do something if the user isnt rotating the model / playing the frames
                    let validIndex = -1;
                    for (let i = 0; i < intersectedItems.length; i++) {
                        if (intersectedItems[i].object.visible === true) {
                            validIndex = i;
                            break;
                        }
                    }
                    if (!mouseIsDown && validIndex !== -1) {
                        //Change back old item to normal material
                        if (overlaySelectedItem !== null && intersectedItems[validIndex].object.id !== overlaySelectedItem.id) {
                            overlaySelectedItem.material = overlayMaterials[overlaySelectedItem.id];
                        }
                        //highlight new hovered item
                        overlaySelectedItem = intersectedItems[validIndex].object;
                        overlaySelectedItem.material = overlayHighlightMaterials[overlaySelectedItem.id];
                        //highlight outline for hovered item
                        outlinePassHighlight.selectedObjects = [overlaySelectedItem];
                        //unit detail display
                        if(overlaySelectedItem.name.startsWith('tooltip_')) {
                            //console.log('I AM A HIGHLIGHT POINT');
                            const screenCoord = worldToScreen(overlaySelectedItem.position, camera, renderer);
                            onTooltipActivated({on: true, src:'', text:tooltipMapping[overlaySelectedItem.name], x: screenCoord.x, y: screenCoord.y});
                        }
                    }
                } else {
                    //Change back old item to normal material
                    if (overlaySelectedItem !== null) {
                        overlaySelectedItem.material = overlayMaterials[overlaySelectedItem.id];
                        //highlight outline for hovered item
                        outlinePassHighlight.selectedObjects = [];
                        //hide unit details
                        ///
                    }
                    overlaySelectedItem = null;
                    onTooltipActivated({on: false, src:'', text:'', x: 0, y: 0});
                }
            }

            function calculatePanSpeed(currentZoom, minZoom, maxZoom, basePanSpeed) {
                // Ensure zoom stays within bounds
                const clampedZoom = Math.max(minZoom, Math.min(currentZoom, maxZoom));

                // Scale the pan speed inversely with zoom level
                const adjustedPanSpeed = basePanSpeed * (maxZoom - clampedZoom) / (maxZoom - minZoom);

                return adjustedPanSpeed;
            }

            let lastFrameTime = 0;
            const fps = 60; // Desired FPS
            const frameTime = 1000 / fps; // Time per frame in ms
            let panSpeed = 0.05;
            const zoomCompensate = 0.5;
            let animate = function () {

                const now = performance.now();
                const deltaTime = now - lastFrameTime;

                if (deltaTime > frameTime) {
                    if (camera && panCollider) {
                        //Pan
                        if (isPanning) {
                            let newPos = cameraPosPanStart.clone();
                            let mouseOffset = mousePosition.clone();
                            mouseOffset.sub(mousePositionPanStart);
                            mouseOffset.multiplyScalar(-1);
                            mouseOffset.multiplyScalar(calculatePanSpeed(camera.zoom, zoomInLimit, zoomOutLimit, panSpeed));
                            //console.log(mouseOffset);
                            newPos.add(mouseOffset);
                            camera.position.copy(newPos);//.lerp(newPos, 0.02); // Adjust the alpha value (0.1) as needed for smoothness
                            camera.updateProjectionMatrix();

                            // Cast rays individually and check results
                            //console.log('Casting rays from viewport corners:');
                            // Calculate the 3D positions of the 2D screen corners in world space
                            const top = getWorldPosition(0, 1, camera);
                            const bottom = getWorldPosition(0, -1, camera);
                            const left = getWorldPosition(-1, 0, camera);
                            const right = getWorldPosition(1, 0, camera);

                            if (left.x < mapPlaneBox.min.x || right.x > mapPlaneBox.max.x) {
                                //console.log('HAS EXITED BORDER (X AXIS)');
                                camera.position.x = clamp(camera.position.x, mapPlaneBox.min.x * camera.zoom * zoomCompensate, mapPlaneBox.max.x * camera.zoom * zoomCompensate);
                                camera.updateProjectionMatrix();
                            }
                            if (bottom.z > mapPlaneBox.max.z || top.z < mapPlaneBox.min.z) {
                                //console.log('HAS EXITED BORDER (Z AXIS)');
                                camera.position.z = clamp(camera.position.z, mapPlaneBox.min.z * camera.zoom * zoomCompensate, mapPlaneBox.max.z * camera.zoom * zoomCompensate);
                                camera.updateProjectionMatrix();
                            }

                            //updateBoxPosition(top, '1', x1);
                            //updateBoxPosition(bottom, '2', x2);
                            //updateBoxPosition(mapPlaneBox.min, '3', x3);
                            //updateBoxPosition(mapPlaneBox.max, '4', x4);
                        }
                    }
                    //
                    requestAnimationFrame(animate);
                    //Match camera to html element aspects and sizes
                    RefreshCameraProjection();
                    //Carry out 3D raycast checks and changes
                    RaycastCheck();

                    //Render
                    effectComposer.render();
                }
            };
            animate();
            //('animate ran');
            //DATA LINK AND FUNCTION
            //Filter
            function ApplySceneFilter (projectData, sceneFilter) {
                //console.log("xxxxxxxxxx", projectData);
                //while(overlayItemsReady === false) {
                    //console.log('setting up scene...');
                //}
                //console.log("FILTERS:", sceneFilter);
                const map300s = new Map([
                    ["301", true],
                    ["302", true],
                    ["303", true],
                    ["304", true],
                    ["305", true],
                    ["306", true],
                ]);

                const map400s = new Map([
                    ["401", true],
                    ["402", true],
                    ["403", true],
                    ["404", true],
                    ["405", true],
                    ["406", true],
                ]);

                for(let i = 0; i < projectData.units.length; i++) {
                    const unit = projectData.units[i];

                    let visState = true;

                    //CHeck Type
                    //console.log(unit.name, unit.type, unit.status);
                    if(sceneFilter.types.includes(unit.type.toLowerCase())) {

                    } else {
                        visState = false;
                    }

                    //Check Features
                    if(sceneFilter.waterFront) {
                        if(unit.waterFront === "no") {
                            visState = false;
                        }
                    }
                    if(sceneFilter.oceanView) {
                        if(unit.oceanView === "no") {
                            visState = false;
                        }
                    }
                    if(sceneFilter.bayView) {
                        if(unit.bayView === "no") {
                            visState = false;
                        }
                    }

                    //Check price renage
                    if(unit.totalPrice < sceneFilter.minPrice || unit.totalPrice > sceneFilter.maxPrice) {
                        visState = false;
                    }

                    //Cheeck sale status
                    if(sceneFilter.status.includes(unit.status.toLowerCase())) {

                    } else {
                        visState = false;
                    }

                    //console.log(unit.name, unit.type, visState);
                    let sceneNode = scene.getObjectByName(unit.name.toString());
                    if(sceneNode !== undefined) {
                        sceneNode.visible = visState;
                    }

                    //
                    if(map300s.has(unit.name.toString())) {
                        //console.log(unit.name.toString(),'SETTING VIS STATE', visState);
                        map300s.set(unit.name.toString(), visState);
                    }
                    if(map400s.has(unit.name.toString())) {
                        //console.log(unit.name.toString(),'SETTING VIS STATE', visState);
                        map400s.set(unit.name.toString(), visState);
                    }

                    filterIsDirtyRef.current = false;
                }

                let sceneNode = scene.getObjectByName('301,302,303,304,305,306');
                if(sceneNode !== undefined) {
                    sceneNode.visible = false;
                    for (const [key, value] of map300s) {
                        if (value) {
                            sceneNode.visible = true;
                            //console.log(key, value);
                            break;
                        }
                    }
                }

                sceneNode = scene.getObjectByName('401,402,403,404,405,406');
                if(sceneNode !== undefined) {
                    sceneNode.visible = false;
                    for (const [key, value] of map400s) {
                        if (value) {
                            sceneNode.visible = true;
                            //console.log(key, value);
                            break;
                        }
                    }
                }
            }

            setApplySceneFilter(()=> ApplySceneFilter);

            setLoadComplete(true);

            // Cleanup on unmount
            return () => {
                threeOverlayElemDiv.removeChild(renderer.domElement);
                frameElemDiv.removeEventListener("pointerdown", OnPointerDownInFrames);
                frameElemDiv.removeEventListener("pointermove", OnPointerMoveInFrames);
                frameElemDiv.removeEventListener("pointerup", OnPointerUpInFrames);
                frameElemDiv.removeEventListener("pointerleave", OnPointerLeaveInFrames);
                frameElemDiv.removeEventListener("wheel", OnMouseWheelInFrames);
                //pan
                frameElemDiv.removeEventListener('pointerdown', handlePanStart);
                frameElemDiv.removeEventListener('pointermove', handlePanMove);
                frameElemDiv.removeEventListener('pointerup', handlePanEnd);
            };
        }

        // Start the check and run threejs code
        checkAllRefs();
    }, []);

    return (
        <>
            <div onClick={()=>{}} ref={frameElemDivRef} className="FrameElemDiv" draggable="false">
                <img ref={frameElemImgRef} className="BackgroundFrameImg"  draggable="false"/>
            </div>
            <div onClick={()=>{}} ref={threeOverlayElemDivRef} className="ThreeOverlay" draggable="false"/>
        </>

    );
});

export default ThreeAnim;
